// File: src/components/mixer/EventDetailsCard.jsx
import React, { useState, useMemo } from 'react';
import { FaCalendar, FaMapMarkerAlt, FaInfoCircle } from 'react-icons/fa';
import { LuChevronRight, LuChevronDown } from 'react-icons/lu';

const styles = {
  container: {
    boxSizing: 'border-box',
    maxWidth: 680,
    width: '94%',
    margin: '12px auto 0',
    padding: '0 12px',
    fontFamily:
      "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  
  dividerRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '.8rem',
    color: '#616161',
    fontWeight: 700,
    marginBottom: 18,
    letterSpacing: '.05em',
    textTransform: 'uppercase',
  },
  dividerLine: { flex: 1, height: 1, backgroundColor: '#eee' },

  summaryCard: {
    padding: 14,
    backgroundColor: '#fff',
    borderRadius: 12,
    border: '0.5px solid #eee',
    marginBottom: 18,

  },

  /* Header: [icon box] [title + venue] [LuChevronRight button] */
  headerRow: {
    display: 'grid',
    gridTemplateColumns: 'auto 1fr auto',
    gap: 10,
    alignItems: 'center',
    marginBottom: 8,
  },

  /* Icon box (matches Instructions banner language) */
  iconBox: {
    width: 38,
    height: 38,
    borderRadius: 10,
    display: 'grid',
    placeItems: 'center',
    background: '#fff',
    border: '0.5px solid #eee',
  },

  titleWrap: {
    minWidth: 0,
  },
  headline: {
    fontSize: 12.5,
    fontWeight: 800,
    color: '#111',
    margin: 0,
    letterSpacing: '-0.01em',
    lineHeight: 1.2,
  },
  subVenue: {
    margin: '3px 0 0',
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1.3,
    color: '#222',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  // styles.ArrowCircleBtn (replace or extend)
ArrowCircleBtn: {
  width: 28,
  height: 28,
  borderRadius: '50%',
  border: '1px solid #ddd',
  backgroundColor: 'transparent',
  cursor: 'pointer',
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
  padding: 0,        // NEW
  lineHeight: 0,     // NEW
  color: '#111',     // let react-icons inherit currentColor
},

chevWrap: {
  width: 14,
  height: 14,
  display: 'grid',
  placeItems: 'center',
},
chevIcon: {
  display: 'block',
  width: '100%',
  height: '100%',
  // optional micro-nudge if needed:
  // transform: 'translateY(0.5px)',
},

  /* Description (left edge aligns with icon box) */
  description: {
    fontSize: 11.5,
    color: '#475569',
    margin: '2px 0 10px',
    lineHeight: 1.5,
  },

  /* Chips */
  chipRow: {
    display: 'flex',
    gap: '0.5rem',
    overflowX: 'auto',
  },
  grayChip: {
    display: 'inline-flex',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    color: '#111',
    fontSize: '.7rem',
    fontWeight: 600,
    padding: '6px 7px',
    borderRadius: 6,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
};

export default function EventDetailsCard({ event }) {
  const [showFullDesc, setShowFullDesc] = useState(false);

  const dateTimeStr = `${event.eventDate}, ${event.startTime12} – ${event.endTime12}`;
  const truncatedDesc =
    event.description?.length > 95
      ? event.description.slice(0, 95) + '...'
      : event.description;

  const venue = useMemo(() => {
    return (
      event.hostedAt ||
      event.venue ||
      (typeof event.address === 'string' ? event.address.split(',')[0] : '') ||
      ''
    );
  }, [event]);

  return (
    <div style={styles.container}>
      <div style={styles.dividerRow}>
        <div style={styles.dividerLine} />
        <div style={styles.dividerLine} />
      </div>

      <div style={styles.summaryCard}>
        {/* Header: Icon box • Title (+Venue) • Chevron toggle */}
        <div style={styles.headerRow}>
          <div style={styles.iconBox} aria-hidden>
            <FaInfoCircle style={{ fontSize: 16, color: '#475569' }} />
          </div>

          <div style={styles.titleWrap}>
            <p style={styles.headline} title={event.name}>{event.name}</p>
            {venue ? (
              <div style={styles.subVenue} title={venue}>
                {venue}
              </div>
            ) : null}
          </div>

          <button
  style={styles.ArrowCircleBtn}
  onClick={() => setShowFullDesc(v => !v)}
  aria-label={showFullDesc ? 'Show less' : 'Show more'}
  aria-expanded={showFullDesc}
  aria-controls="event-details-description"
>
  <span style={styles.chevWrap}>
    {showFullDesc
      ? <LuChevronDown size={14} style={styles.chevIcon} />
      : <LuChevronRight size={14} style={styles.chevIcon} />
    }
  </span>
</button>
        </div>

        {/* Description — left-aligned with icon box */}
        <p id="event-details-description" style={styles.description}>
          {showFullDesc ? event.description : truncatedDesc}
        </p>

        {/* Chips */}
        <div style={styles.chipRow} className="hide-scrollbar">
          <div style={styles.grayChip}>
            <FaCalendar style={{ marginRight: 6, fontSize: '.8rem' }} />
            {dateTimeStr}
          </div>
          {event.address && (
            <div style={styles.grayChip}>
              <FaMapMarkerAlt style={{ marginRight: 6, fontSize: '.8rem' }} />
              {event.address}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
